import { useContext, useEffect, useState } from "react";
import { BatchPrintListItem } from "../../../../fms.data/models/BatchPrintListItem";
import JobListViewContext, { RightClickContext } from "../../../common/JobListViewContext";
import { useHistory } from "react-router";
import { NavigationType, openNewPage } from "../../../../util/navigationHelpers";
import { ActionButtonItem, borderColors, splitMenuItemsIntoSections } from "../../../../ui/ActionButton";
import ToolboxMenuItem from "../../../../uiControls/ToolboxMenuItem";
import Menu, { IfmsMenuItem } from "../../../../ui/menu/Menu";
import { ViewIconButton2 } from "../../../../ui/IconButton";
import ListViewShortcutMenu from "../../../common/ListViewShortcutMenu";
import useFunctionKeys from "../../../../hooks/useFunctionKeys";
import Select from "../../../../ui/Select";
import AbstractListView, { DataFetchResult, ListCustomFilterRenderer, ListOrderBy, ListRowRenderer } from "../../../common/AbstractListView";
import { JobNames } from "../../../../framework.core/JobNames";
import { Search } from "../../../../filtering";
import * as XCol from '../../../../framework.core/XExportColumn';
import { DateRenderer, DateTimeIncludeSecRenderer, FloatRenderer, TextAlignLeftRenderer, TextRenderer } from "../../../../uiControls/SmartTableRenderers";
import constants from '../../../../constants';
import { TableColumn, TableSettings } from "../../../../tableSettings";
import { batchPrintHistoryList } from "../../tableSettings";

function getViewUrl(row: BatchPrintListItem | null | undefined) {
  if (!row || !row.id) return '';
  const docTypeParam = row.docType === 'CRDB' ? 'crdb' : 'invoice';
  const parentOp = new URLSearchParams(window.location.search).get('parentOp');
  return `/accounting/batchPrint/${docTypeParam}/view/${row.id}${parentOp ? `?parentOp=${parentOp}` : ''}`;
}

function useSelectedOperations() {
  const jobListContext = useContext(JobListViewContext);
  const history = useHistory();
  const selectedElement = jobListContext.selectedElement as BatchPrintListItem;

  async function view() {
    const viewUrl = getViewUrl(selectedElement);
    if (!viewUrl) {
      return;
    }

    openNewPage(history, viewUrl, NavigationType.newPage);
  }

  return {view};
}

function RightClickMenu({dismiss}:{dismiss: VoidFunction}) {
  const { view } = useSelectedOperations();
  const jobListContext = useContext(JobListViewContext);

  const items = [
    { label: 'View', shortcut: 'F9', separator: false },
    { label: 'Search', shortcut: 'F5', separator: false },
  ];

  const menuItems: ActionButtonItem[] = items.map((i, index) =>({
    label: <ToolboxMenuItem name="batchPrint-list-sub-menu" width="190px" label={i.label} shortcut={i.shortcut} />,
    separator: i.separator,
    selected: false,
    index,
  }));

  function onMenuItemSelected(item:IfmsMenuItem) {
    switch (item.index) {
      case 0:
        view();
        break;
      case 1:
        jobListContext.triggerTableSearch('F5');
        break;
      default:
        break;
    }
    dismiss();
  }

  return (
    <Menu
      variant="plain"
      specialMenu='rightClickContext'
      items={splitMenuItemsIntoSections(menuItems)}
      mode="vertical"
      onMenuItemSelected={onMenuItemSelected}
      subMenuStyle={{
        minWidth: 150,
        whiteSpace: 'nowrap',
      }}
      menuStyle={(i: number) => ({
        maxHeight: '300px',
        overflowY: 'hidden',
        padding: 0,
        borderTop: `3px solid ${borderColors[i % borderColors.length]}`,
        paddingTop: 10,
      })}
      dismiss={dismiss}
    />
  );
}

function ListCommands() {
  const jobListContext = useContext(JobListViewContext);
  const selectedElement = jobListContext.selectedElement as BatchPrintListItem;

  const { view } = useSelectedOperations();
  const hasSelectedRow = !!selectedElement;

  return (
     <div className='ifms-common-no-vertical-separator-gap-margin'>
      <ViewIconButton2 name='batchPrint-list-view-button' disabled={!hasSelectedRow} onClick={view} useText />
    </div>
  );
}

function ListToolbar() {
  return (
    <>
      <ListViewShortcutMenu />
    </>
  );
}

function ListViewExtras() {
  useFunctionKeys({onFunction: onFunctionKey});
  const {view} = useSelectedOperations();

  const [unprocessedFkey, setUnprocessedFkey] = useState<string | null>(null);

  useEffect(() => {
      if (!unprocessedFkey) {
        return;
      }

      doProcessFkey();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unprocessedFkey]
  );

  function doProcessFkey() {
    if (unprocessedFkey === "F9") {
      view();
    }

    setUnprocessedFkey(null);
  }

  function onFunctionKey(fkey: string) {
    if (['F9'].indexOf(fkey) >= 0) {
      setUnprocessedFkey(fkey);
      return true;
    }

    return false;
  }

  return null;
}

function RangeTypeFilterRenderer({value, onChange}:any) {
  return (
    <Select
      name='ifms-filter-rangeType-select'
      value={value}
      onChange={e => onChange(e.target.value)}
      items={[
        { value: '', label: 'ALL'},
        { value: 'Date', label: 'Date'},
        { value: 'Number', label: 'Number'},
      ]}
    />
  );
}

function StoppedFilterRenderer({value, onChange}:any) {
  return (
    <Select
      name='ifms-filter-stopped-select'
      value={value}
      onChange={e => onChange(e.target.value)}
      items={[
        { value: '', label: 'ALL'},
        { value: 'Y', label: 'Y'},
      ]}
    />
  );
}

/**
 * Invoice Batch Print History List — BatchRecog History 목록과 같은 구성
 *
 * @class ListView
 * @extends {AbstractListView}
 */
class ListView extends AbstractListView {

  async fetchAndFormatData(api: any, search: Search, pageNum: number, limit: number): Promise<DataFetchResult> {
    const apiResult = await api.getListItems(
      JobNames.BatchPrintHistory,
      'BatchPrintListItem',
      BatchPrintListItem.calculateSearchParameters(search, BatchPrintListItem.MapSearchFields),
      pageNum,
      limit,
      search.pageSize,
      search.pagePerBatch,
      );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return {data: [], totalCount: 0};
    }

    return {data: BatchPrintListItem.formatData(apiResult.entities), totalCount: apiResult.totalCount};
  }

  async exportFormatDataWithExcel(api: any, search: Search, pageNum: number, limit: number, pageSize: number, pagePerBatch: number): Promise<Array<any>> {
    const apiResult = await api.getListItems(JobNames.BatchPrintHistory, 'BatchPrintListItem',
      BatchPrintListItem.calculateSearchParameters(search, BatchPrintListItem.MapSearchFields),
      pageNum,
      limit,
      pageSize,
      pagePerBatch
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return [];
    }

    return BatchPrintListItem.formatData(apiResult.entities);
  }

  getCustomFilterRenderers(): ListCustomFilterRenderer[] {
    return [
      {
        accessor: 'rangeType',
        renderer: RangeTypeFilterRenderer,
        searchValueExtractor: (v:any|null|undefined) => v,
      },
      {
        accessor: 'stopped',
        renderer: StoppedFilterRenderer,
        searchValueExtractor: (v:any|null|undefined) => v,
      },
    ];
  }

  getInfoAreaEntityId() {
    return constants.infoAreaEntity.oceanImportMbl;   // BatchRecog History 와 동일
  }

  getRowRenderers(): ListRowRenderer[] {
    return BatchPrintListItem.getExportCollection().map(x => {
      const colRenderer:ListRowRenderer = { accessor: x.dataIndex, Cell: TextAlignLeftRenderer };
      if(x.align === XCol.XAlignType.Center) colRenderer.Cell = TextRenderer;
      else if(x.type === XCol.XCellType.Date) colRenderer.Cell = DateRenderer;
      else if(x.type === XCol.XCellType.DateTime) colRenderer.Cell = DateTimeIncludeSecRenderer;
      else if(x.type === XCol.XCellType.Double) colRenderer.Cell = (props: any) => <FloatRenderer {...props} decimals={0} />;
      return colRenderer;
    });
  }

  getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'actionDate',
      orderByAscending: false,
    };
  }

  getTableSettings(): TableSettings {
    const tsettings = batchPrintHistoryList;
    const cols = BatchPrintListItem.getExportCollection();
    tsettings.columns = cols.map<TableColumn>(x => {
      return { ...x, accessor: x.dataIndex, Header: x.text, filterType: undefined, filterTypeV2: x.type};
    });
    return tsettings;
  }

  renderCommands() {
    return (
      <ListCommands/>
    );
  }

  renderBeforeConfigureButton() {
    return (
      <ListToolbar/>
    );
  }

  renderExtras() {
    return <ListViewExtras/>
  }

  getViewUrlFor(row: BatchPrintListItem) {
    return getViewUrl(row);
  }

  getListViewTitle() {
    return 'Invoice Batch Print History';
  };

  resizetitleLabel(): boolean{
    return true;
  }

  hasRightClickContext(): boolean {
    return true;
  }

  renderRightClickContext(context: RightClickContext, dismiss: VoidFunction): React.ReactNode {
    return <RightClickMenu dismiss={dismiss} />;
  }

  hasMemo(): boolean {
    return false;
  }
}

export default ListView;